import { all, call, put, takeLatest } from 'redux-saga/effects';

import { actions as localzIntegrationActions } from 'react/api/actions/localzIntegration';
import defaultFetch from 'react/api/sagas/fetch/defaultFetch';
import { getResourceTo } from 'react/utils/utils';

const {
  fetchIntegration,
  setConnectionState,
  connectIntegration,
  clearIntegrationDisableState,
  setIsLoadingIntegration,
  updateIntegration,
  disableIntegration,
  setDisableState,
  clearIntegrationConnectState
} = localzIntegrationActions;

export function* fetchIntegrationSaga(action) {
  const { tenant } = action.payload;

  yield put({
    type: setIsLoadingIntegration,
    payload: {
      isLoadingIntegration: true
    }
  });

  const encodedURL = encodeURI(`/IdentityAccess/FindByTenant/${tenant}`);

  const fetchIntegrationsResponse = yield call(defaultFetch, encodedURL);

  if (!fetchIntegrationsResponse?.error) {
    const {
      region,
      projectId,
      apikey,
      enabled,
      id,
      organizations = []
    } = fetchIntegrationsResponse;

    yield put({
      type: updateIntegration,
      payload: {
        integration: {
          region,
          projectId,
          apikey,
          tenant,
          id,
          organizations,
          enabled
        }
      }
    });
  }

  if (fetchIntegrationsResponse?.error) {
    yield put({
      type: setConnectionState,
      payload: {
        isConnecting: false,
        error: null,
        success: null
      }
    });
  }

  yield put({
    type: setIsLoadingIntegration,
    payload: {
      isLoadingIntegration: false
    }
  });
}

export function* watchFetchIntegration() {
  yield takeLatest(fetchIntegration, fetchIntegrationSaga);
}

export function* connectIntegrationSaga(action) {
  const {
    region,
    apikey,
    projectId,
    tenant,
    enabled,
    name,
    isConnected,
    organizations
  } = action.payload;

  const requestMethod = isConnected ? 'PUT' : 'POST';
  const encodedURL = encodeURI('/IdentityAccess');

  yield put({
    type: setConnectionState,
    payload: {
      isConnecting: true,
      success: null,
      error: null
    }
  });

  const response = yield call(
    defaultFetch,
    encodedURL,
    {
      region,
      apikey,
      projectId,
      tenant,
      enabled,
      organizations
    },
    requestMethod
  );

  if (!response?.error) {
    yield all([
      put({
        type: updateIntegration,
        payload: {
          integration: {
            name,
            region: response.region,
            apikey: response.apikey,
            projectId: response.projectId,
            tenant: response.tenant,
            id: response.id,
            organizations: response.organizations || []
          },
          enabled: response.enabled
        }
      }),
      put({
        type: setConnectionState,
        payload: {
          isConnecting: false,
          error: null,
          success: {
            message: getResourceTo(
              'IntegrationModal.ConnectIntegration.SuccessMessage'
            )
          }
        }
      })
    ]);
  }

  if (response?.error) {
    yield put({
      type: setConnectionState,
      payload: {
        isConnecting: false,
        error: {
          message: getResourceTo(
            'IntegrationModal.ConnectIntegration.CreateError'
          )
        },
        success: null
      }
    });
  }
}

export function* watchConnectIntegration() {
  yield takeLatest(connectIntegration, connectIntegrationSaga);
}

export function* disableIntegrationSaga(action) {
  const { tenant } = action.payload;

  yield put({
    type: setDisableState,
    payload: {
      isDisabling: true,
      error: null,
      success: null
    }
  });

  const encodedURL = encodeURI('/IdentityAccess/ToggleFeature');

  const response = yield call(
    defaultFetch,
    encodedURL,
    {
      tenant,
      enabled: false
    },
    'POST'
  );

  if (!response?.error) {
    yield all([
      put({
        type: updateIntegration,
        payload: {
          integration: {
            region: response.region,
            apikey: response.apikey,
            projectId: response.projectId,
            tenant: response.tenant,
            id: response.id,
            organizations: response.organizations || []
          },
          enabled: false
        }
      }),
      put({
        type: setDisableState,
        payload: {
          isDisabling: false,
          error: null,
          success: true
        }
      })
    ]);
  }

  if (response?.error) {
    yield put({
      type: setDisableState,
      payload: {
        isDisabling: false,
        error: {
          message: getResourceTo(
            'IntegrationModal.DisableIntegrationModal.ErrorMessage'
          )
        },
        success: null
      }
    });
  }
}

export function* watchDisableIntegration() {
  yield takeLatest(disableIntegration, disableIntegrationSaga);
}

export function* clearDisableStatusSaga() {
  yield put({
    type: setDisableState,
    payload: {
      isDisabling: false,
      success: null,
      error: null
    }
  });
}

export function* clearConnectStatusSaga() {
  yield put({
    type: setConnectionState,
    payload: {
      isConnecting: false,
      success: null,
      error: null
    }
  });
}

export function* watchClearDisableStatus() {
  yield takeLatest(clearIntegrationDisableState, clearDisableStatusSaga);
}

export function* watchClearConnectStatus() {
  yield takeLatest(clearIntegrationConnectState, clearConnectStatusSaga);
}

export default function* () {
  yield all([
    watchFetchIntegration(),
    watchConnectIntegration(),
    watchDisableIntegration(),
    watchClearConnectStatus(),
    watchClearDisableStatus()
  ]);
}
